import { syncWishlistButtons } from '@theme/omerella-wishlist';

/** @type {number | undefined} */
let addToCartShiverTimer;

class OmerellaProductPage {
  connected = false;
  #variantListenerBound = false;

  init() {
    if (!document.querySelector('.omerella-product')) return;

    this.#markBuySectionReady();
    this.#placeSoldIndicator();
    this.#injectWishlistButton();
    this.#watchAcceleratedCheckout();
    this.#fixQuantitySelector();
    this.#updateSoldOutLabels();
    this.#updateVariantLegends();
    syncWishlistButtons();

    if (this.connected) return;
    this.connected = true;

    this.#setupNotifyButton();
    this.#setupDescriptionReadMore();
    this.#setupPromoCopy();
    this.#setupAddToCartShiver();
    this.#watchRecommendationsWishlist();
    this.#setupQuantitySelector();
    this.#setupInstantVariantLabels();

    if (!this.#variantListenerBound) {
      this.#variantListenerBound = true;
      document.addEventListener('variant:update', () => {
        this.#placeSoldIndicator();
        this.#injectWishlistButton();
        this.#updateSoldOutLabels();
        this.#updateVariantLegends();
        this.#fixQuantitySelector();
        this.#watchAcceleratedCheckout();
        this.#setupAddToCartShiver();
        syncWishlistButtons();
      });
    }

    document.addEventListener('shopify:section:load', (event) => {
      const section = /** @type {HTMLElement | null} */ (event.target);
      if (!section?.classList.contains('omerella-product-recommendations-section')) return;
      syncWishlistButtons();
    });
  }

  #markBuySectionReady() {
    document.querySelector('.omerella-product .product-form-buttons')?.classList.add('is-ready');
  }

  #placeSoldIndicator() {
    const indicator = document.querySelector('.omerella-product [data-sold-indicator]');
    const priceEl =
      document.querySelector('.omerella-product .product-details product-price') ||
      document.querySelector('.omerella-product .product-details .price');

    if (!indicator || !priceEl || indicator.dataset.placed === 'true') return;

    const anchor = priceEl.closest('.text-block') || priceEl.parentElement || priceEl;
    anchor.insertAdjacentElement('afterend', indicator);
    indicator.dataset.placed = 'true';
  }

  #setupInstantVariantLabels() {
    const picker = document.querySelector('.omerella-product variant-picker');
    if (!picker || picker.dataset.omerellaLabelsBound === 'true') return;

    picker.dataset.omerellaLabelsBound = 'true';
    picker.addEventListener('change', (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
      this.#updateLegendForFieldset(input.closest('.variant-option--buttons'), input.value);
    });
  }

  /**
   * @param {Element | null} fieldset
   * @param {string} value
   */
  #updateLegendForFieldset(fieldset, value) {
    const legend = fieldset?.querySelector('legend');
    if (!legend || !value) return;

    const optionName = legend.textContent.split(':')[0].trim();
    legend.innerHTML = `${optionName}: <span>${value}</span>`;
  }

  #setupQuantitySelector() {
    this.#fixQuantitySelector();
  }

  #fixQuantitySelector() {
    const selector = document.querySelector('.omerella-product quantity-selector-component');
    if (!selector) return;

    selector.serverDisabledMinus = false;
    selector.serverDisabledPlus = false;
    selector.updateButtonStates?.();
  }

  #updateVariantLegends() {
    document.querySelectorAll('.omerella-product .variant-option--buttons').forEach((fieldset) => {
      const checked = fieldset.querySelector('input:checked');
      if (!checked) return;
      this.#updateLegendForFieldset(fieldset, checked.value);
    });
  }

  #injectWishlistButton() {
    const formButtons = document.querySelector('.omerella-product .product-form-buttons');
    const productForm = document.querySelector('.omerella-product product-form-component');
    if (!formButtons || formButtons.querySelector('.omerella-product-wishlist-btn')) return;

    const productId = productForm?.dataset.productId;
    if (!productId) return;

    const title = document.querySelector('.omerella-product h1')?.textContent?.trim() || '';
    const url = productForm?.dataset.productUrl || window.location.pathname;
    const image = document.querySelector('.omerella-product .product-media-container img')?.src || '';
    const price = document.querySelector('.omerella-product product-price, .omerella-product .price')?.textContent?.trim() || '';

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'omerella-wishlist-btn omerella-product-wishlist-btn';
    button.dataset.productId = productId;
    button.dataset.productTitle = title;
    button.dataset.productUrl = url;
    button.dataset.productImage = image;
    button.dataset.productPrice = price;
    button.dataset.addLabel = 'Add to wishlist';
    button.dataset.removeLabel = 'Remove from wishlist';
    button.setAttribute('aria-pressed', 'false');
    button.setAttribute('aria-label', 'Add to wishlist');
    button.innerHTML = `
      <svg class="omerella-wishlist-btn__icon omerella-wishlist-btn__icon--outline" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true">
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
      </svg>
    `;

    formButtons.appendChild(button);
  }

  #setupDescriptionReadMore() {
    document.querySelectorAll('.omerella-product [data-read-more]').forEach((container) => {
      if (container.dataset.readMoreBound === 'true') return;
      container.dataset.readMoreBound = 'true';

      const toggle = container.querySelector('[data-read-more-toggle]');
      const content = container.querySelector('[data-description-content]');
      if (!(toggle instanceof HTMLButtonElement) || !(content instanceof HTMLElement)) return;

      const syncToggleVisibility = () => {
        content.classList.add('is-collapsed');
        content.classList.remove('is-expanded');

        const needsToggle = content.scrollHeight > content.clientHeight + 1;
        if (!needsToggle) {
          toggle.hidden = true;
          content.classList.remove('is-collapsed');
          return;
        }

        toggle.hidden = false;
        toggle.textContent = 'Read more';
        toggle.setAttribute('aria-expanded', 'false');
      };

      syncToggleVisibility();

      toggle.addEventListener('click', () => {
        const willExpand = toggle.getAttribute('aria-expanded') !== 'true';

        content.classList.toggle('is-collapsed', !willExpand);
        content.classList.toggle('is-expanded', willExpand);
        toggle.textContent = willExpand ? 'Read less' : 'Read more';
        toggle.setAttribute('aria-expanded', willExpand ? 'true' : 'false');
      });
    });
  }

  #setupNotifyButton() {
    const notify = document.querySelector('.omerella-product-notify');
    if (!(notify instanceof HTMLButtonElement)) return;

    notify.addEventListener('click', () => {
      const email = window.prompt(notify.dataset.prompt || 'Enter your email to get notified:');
      if (!email) return;
      notify.textContent = notify.dataset.successLabel || 'Thank you! We will notify you.';
      notify.disabled = true;
    });
  }

  #watchAcceleratedCheckout() {
    const checkout = document.querySelector('.omerella-product .accelerated-checkout-block');
    if (!(checkout instanceof HTMLElement) || checkout.dataset.omerellaCheckoutWatched === 'true') return;

    checkout.dataset.omerellaCheckoutWatched = 'true';

    const hasButton = () =>
      Boolean(
        checkout.querySelector(
          '.shopify-payment-button__button, shopify-accelerated-checkout, .shopify-payment-button'
        )
      );

    const sync = () => {
      if (!checkout.isConnected) {
        observer?.disconnect();
        return false;
      }

      if (checkout.hasAttribute('hidden') && !hasButton()) {
        checkout.classList.remove('is-loading', 'is-empty');
        return false;
      }

      if (hasButton()) {
        checkout.classList.remove('is-loading', 'is-empty');
        checkout.hidden = false;
        return true;
      }

      checkout.classList.add('is-loading');
      checkout.classList.remove('is-empty');
      checkout.hidden = false;
      return false;
    };

    if (sync()) return;

    const observer = new MutationObserver(() => {
      if (sync()) observer.disconnect();
    });

    observer.observe(checkout, { childList: true, subtree: true });

    window.setTimeout(() => {
      observer.disconnect();
      if (!hasButton()) {
        checkout.classList.remove('is-loading');
        checkout.classList.add('is-empty');
        checkout.hidden = true;
      }
    }, 4000);
  }

  #watchRecommendationsWishlist() {
    const el = document.querySelector('product-recommendations.omerella-product-recommendations');
    if (!(el instanceof HTMLElement) || el.dataset.wishlistWatch === 'true') return;

    el.dataset.wishlistWatch = 'true';

    const observer = new MutationObserver(() => syncWishlistButtons());
    observer.observe(el, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-recommendations-performed'],
    });
  }

  #setupAddToCartShiver() {
    const addBtn = document.querySelector('.omerella-product .add-to-cart-button');
    if (!(addBtn instanceof HTMLButtonElement) || addBtn.dataset.shiverBound === 'true') return;

    addBtn.dataset.shiverBound = 'true';

    const runShiver = () => {
      if (addBtn.disabled) return;
      addBtn.classList.add('is-shivering');
      window.setTimeout(() => addBtn.classList.remove('is-shivering'), 700);
    };

    const scheduleShiver = () => {
      const delay = 8000 + Math.floor(Math.random() * 2001);
      addToCartShiverTimer = window.setTimeout(() => {
        runShiver();
        scheduleShiver();
      }, delay);
    };

    scheduleShiver();

    addBtn.addEventListener('click', () => {
      if (addToCartShiverTimer) window.clearTimeout(addToCartShiverTimer);
      addBtn.classList.remove('is-shivering');
    });
  }

  #setupPromoCopy() {
    const copyBtn = document.querySelector('.omerella-product-promo__copy');
    if (!(copyBtn instanceof HTMLButtonElement)) return;

    copyBtn.addEventListener('click', async () => {
      const code = copyBtn.dataset.code;
      if (!code) return;

      try {
        await navigator.clipboard.writeText(code);
      } catch {
        /* ignore */
      }

      copyBtn.textContent = copyBtn.dataset.copiedLabel || 'Copied!';
      copyBtn.classList.add('is-copied');
      window.setTimeout(() => {
        copyBtn.textContent = copyBtn.dataset.defaultLabel || 'Copy';
        copyBtn.classList.remove('is-copied');
      }, 2000);
    });
  }

  #updateSoldOutLabels() {
    const addBtn = document.querySelector('.omerella-product .add-to-cart-button');
    const notify = document.querySelector('.omerella-product-notify');
    if (!(addBtn instanceof HTMLButtonElement) || !(notify instanceof HTMLButtonElement)) return;

    const soldOut = addBtn.disabled;
    notify.hidden = !soldOut;

    if (soldOut) {
      const label = addBtn.querySelector('.add-to-cart-text__content span span, .add-to-cart-text__content span');
      if (label) label.textContent = 'OUT OF STOCK';
    } else {
      const label = addBtn.querySelector('.add-to-cart-text__content span span, .add-to-cart-text__content span');
      if (label) label.textContent = 'ADD TO CART';
    }
  }
}

const page = new OmerellaProductPage();

const bootProductPage = () => page.init();

bootProductPage();

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootProductPage);
}

document.addEventListener('shopify:section:load', () => {
  page.connected = false;
  bootProductPage();
});
